import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createGlobalState } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { entitlementsActions, entitlementsSelectors } from 'app-components/flux/entitlements';
import { getLogger, tracker } from 'app-components/utils/core';

import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
// import ZeroStateView from 'components/ZeroStateView';
// import paperAirplaneCrash from 'assets/illustrations/Plane-Crash.png';
import LoadingView from 'components/LoadingView';

import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import { useFetchOnce } from 'app-components/hooks/utils';

import useAuthTokenValidator from './useAuthTokenValidator';
import useAuthURL from './useAuthURL';
import useDatasetHandler from './useDatasetHandler';
import useEntitlementValidator from './useEntitlementValidator';
import useOAuthHandler from './useOAuthHandler';

const logger = getLogger('components/Dialogs/AuthDialog');

export const useGlobalSuppressAuthDialog = createGlobalState(false);

const useStyles = makeStyles()((_theme, dm) => ({
  paper: {
    '@media (max-width: 500px)': {
      margin: 2,
    },
  },
  contentRoot: {
    backgroundColor: dm ? '#22262a' : null,
    '@media (max-width: 500px)': {
      padding: 1,
    },
  },
  loadingView: {
    width: '100%',
    height: '100%',
    position: 'absolute',
  },
}));

const AuthStates = Object.freeze({
  DIALOG_INACTIVE: 'DIALOG_INACTIVE',
  WIDGET_LOADING: 'WIDGET_LOADING',
  WIDGET_FAILED: 'WIDGET_FAILED',
  WIDGET_READY: 'WIDGET_READY',
  TOKEN_LOADING: 'TOKEN_LOADING',
  TOKEN_FAILED: 'TOKEN_FAILED',
});

const AuthDialog = React.memo((props) => {
  const { ...otherProps } = props;
  const dm = useSelector(authSelectors.getAuthTheme);

  const { classes } = useStyles(dm);

  const iframeRef = useRef();
  const [suppressAuthDialog] = useGlobalSuppressAuthDialog();

  const { accessToken } = useSelector(authSelectors.getAuthSession);
  const isLoadingToken = useSelector(authSelectors.getIsLoading);
  const authError = useSelector(authSelectors.getError);

  const [authState, setAuthState] = useState(AuthStates.DIALOG_INACTIVE);

  // const pathname = useLocation()?.pathname;
  // const authNotNeeded = useMemo(() => {
  //   const featurePage = findFeaturePageByPath(pathname);
  //   return featurePage ? featurePage.authNotNeeded : true;
  // }, [pathname]);
  const authNotNeeded = false;

  useAuthTokenValidator({ authNotNeeded });
  useEntitlementValidator();
  useDatasetHandler({ authNotNeeded });
  const authURL = useAuthURL();
  useOAuthHandler();

  // Make sure subscriptions and entitlements are loaded. Both require authentication. Entitlements also require
  // a dataset ID to be set within the auth-session (to support dataset sharing).
  //
  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors, false, true);
  useFetchOnce(entitlementsActions.getEntitlements, entitlementsSelectors, false, false);

  useEffect(() => {
    if (accessToken || authNotNeeded || suppressAuthDialog) {
      setAuthState(AuthStates.DIALOG_INACTIVE); // auth not required
    } else if (authState === AuthStates.DIALOG_INACTIVE) {
      setAuthState(AuthStates.WIDGET_LOADING); // load widget
    } else if (isLoadingToken) {
      setAuthState(AuthStates.TOKEN_LOADING); // show /token progress
    } else if (authError && authState === AuthStates.TOKEN_LOADING) {
      setAuthState(AuthStates.TOKEN_FAILED);
    }
  }, [accessToken, authNotNeeded, suppressAuthDialog, authError, authState, isLoadingToken]);

  useEffect(() => {
    logger.log(`new auth widget state: ${authState}`);
    tracker.track(tracker.events.authWidget, { status: authState });
    if (authState === AuthStates.WIDGET_LOADING) {
      tracker.timeEvent(tracker.events.authWidget);
    }
  }, [authState]);

  const dialogProps = useMemo(() => ({ classes: { paper: classes.paper } }), [classes.paper]);

  if ([AuthStates.DIALOG_INACTIVE, AuthStates.TOKEN_LOADING].includes(authState)) return null;
  return (
    <StdDialog
      disableBackdropClick
      disableEscapeKeyDown
      id='auth-dialog'
      open={authState !== AuthStates.DIALOG_INACTIVE}
      showCloseButton={false}
      size='custom'
      {...dialogProps}
      {...otherProps}
    >
      <DialogContent className={classes.contentRoot}>
        <Box display='flex' flexDirection='column' position='relative'>
          {[AuthStates.WIDGET_LOADING, AuthStates.TOKEN_LOADING].includes(authState) && (
            <LoadingView className={classes.loadingView} />
          )}

          {[AuthStates.WIDGET_LOADING, AuthStates.WIDGET_READY, AuthStates.WIDGET_FAILED].includes(authState) && (
            <iframe
              id='login_frame'
              ref={iframeRef}
              style={{
                opacity: authState === AuthStates.WIDGET_READY || AuthStates.WIDGET_FAILED ? 1 : 0,
                transition: 'all 500ms',
              }}
              title='auth'
              aria-hidden='true'
              width={375}
              height={699}
              seamless
              loading='lazy'
              src={authURL?.href}
              onLoad={() => {
                if (authState !== AuthStates.WIDGET_READY) {
                  setAuthState(AuthStates.WIDGET_READY);
                  setTimeout(() => {
                    const isLoaded = Boolean(iframeRef.current?.contentWindow?.window?.length);
                    if (!isLoaded) {
                      setAuthState(AuthStates.WIDGET_FAILED);
                      // assert(false, 'auth widget load failed');
                    }
                  }, 3000);
                }
              }}
            />
          )}

          {[AuthStates.TOKEN_LOADING, AuthStates.DIALOG_INACTIVE].includes(authState) && (
            <Box width={375} height={699} />
          )}

          {/* TODO: need design to handle errors */}
          {/* [AuthStates.WIDGET_FAILED, AuthStates.TOKEN_FAILED].includes(authState) && (
            <ZeroStateView
              in
              icon={paperAirplaneCrash}
              primary="Whoops... sorry about that!"
              secondary="Try to sign in again"
              button="Sign In"
              report
              onClick={() => setAuthState(AuthStates.WIDGET_LOADING)}
            />
          ) */}
        </Box>
      </DialogContent>
    </StdDialog>
  );
});
AuthDialog.displayName = 'AuthDialog';
export default React.memo(AuthDialog);
